import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { number, object, string } from 'yup';

import { useAlerta, useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, { Campo, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import Modal from 'Component/Modal';
import { useSubscription } from 'Hook/Subscription';
import { Erro } from 'Interface/Requisicao';
import { CartaoDeCredito } from 'Interface/Transacoes';
import {
  useEditarAssinaturaMutation,
  useTrocarCartaoDaAssinaturaMutation,
} from 'Service';
import { BANDEIRAS, MASCARA_DATA_VENCIMENTO } from 'View/constants';
import { errosParaLista } from 'View/functions';

const regras = object({
  nome: string()
    .required('Obrigatório')
    .max(26, 'O nome deve ser igual ao que consta em seu cartão'),
  numero: number().required('Obrigatório'),
  vencimento: string().required('Obrigatório'),
  codigo: number().required('Obrigatório'),
  bandeira: string().required('Obrigatório'),
});

const Visualizar = (): React.ReactElement => {
  const toast = useToast();
  const alerta = useAlerta();
  const subscription = useSubscription();
  const [modal, setModal] = useState<boolean>(false);

  const [editarAssinatura] = useEditarAssinaturaMutation();
  const [trocarCartao] = useTrocarCartaoDaAssinaturaMutation();

  const cancelar = async (): Promise<void> => {
    if (subscription) {
      try {
        await editarAssinatura({ id: subscription.id, status: 0 }).unwrap();
      } catch (erro) {
        toast({
          titulo: 'Erro ao cancelar assinatura',
          conteudo: 'Verifique os logs para maiores detalhes.',
          tema: 'danger',
        });
      }
    }
  };

  const trocar = async (dados: CartaoDeCredito): Promise<void> => {
    if (subscription) {
      try {
        await trocarCartao({
          id: subscription.id,
          cartao: dados,
        }).unwrap();

        setModal(false);

        alerta({
          titulo: 'Troca efetuada com sucesso!',
          conteudo:
            'Agora basta aguardar um prazo de 24 horas para a efetivar.',
          tema: 'success',
        });
      } catch (erros) {
        alerta({
          titulo: 'Erro ao realizar o cadastro da assinatura',
          conteudo: errosParaLista(erros as Erro),
          tema: 'danger',
        });
      }
    }
  };

  return (
    <div
      className="animate__animated animate__fadeIn"
      style={{ minHeight: window.outerHeight }}
    >
      <div
        className="hf-400 d-flex align-items-end pb-5"
        style={{
          backgroundImage:
            'linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 100%), url(/img/meus-cursos.jpg)',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          marginTop: '-130px',
        }}
      >
        <div className="container text-center">
          <h1 className="display-1 text-white">Minha assinatura</h1>
          <p className="lead text-muted">
            Aqui você pode ver a próxima data de cobrança e também cancelar sua
            assinatura se desejar!
          </p>
        </div>
      </div>
      <div className="container pt-5">
        <div className="row justify-content-center">
          <div className="col-md-9">
            <div
              className={`bg-${
                subscription && subscription.status == 1
                  ? 'success'
                  : subscription && subscription.status == 2
                  ? 'warning'
                  : 'danger'
              } bg-opacity-50 border border-3 border-${
                subscription && subscription.status == 1
                  ? 'success'
                  : subscription && subscription.status == 2
                  ? 'warning'
                  : 'danger'
              } border-opacity-75 rounded-3 p-3`}
            >
              <p className="fs-1 text-center">
                {subscription && subscription.status == 0 && (
                  <span className="text-danger ms-3">
                    Assinatura desativada
                  </span>
                )}
                {subscription && subscription.status == 1 && (
                  <span className="text-success ms-3">Assinatura ativa</span>
                )}
                {subscription && subscription.status == 2 && (
                  <>
                    <span className="text-warning ms-3">
                      Erro ao processar o pagamento da sua assinatura
                    </span>
                    <small className="d-block text-white fs-6">
                      Pode ser que no momento em que tentamos passar o cartão
                      você estava sem limite disponível ou o cartão cadastrado
                      não é mais válido. Para resolver o problema você pode
                      tentar cadastrar um novo cartão clicando em &quot;Trocar
                      cartão de crédito&quot;.
                    </small>
                  </>
                )}
              </p>
              <div className="row">
                <div className="col-md-6">
                  <p className="fs-2 text-dark text-center">Início</p>
                  <p className="fs-1 text-center text-white">
                    {subscription &&
                      subscription.inicio
                        ?.data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="fs-2 text-dark text-center">
                    <Icone nome="clock-history" className="me-2" />
                    Próxima cobrança
                  </p>
                  <p className="fs-1 text-center text-white">
                    {subscription &&
                      subscription.proxima_cobranca.data().toLocaleString()}
                  </p>
                </div>
                {subscription && [1, 2].includes(subscription.status || 0) && (
                  <div className="col-md-12 d-flex justify-content-center mt-5">
                    <Botao
                      lg
                      tema={subscription.status == 1 ? 'success' : 'warning'}
                      texto="Trocar cartão de crédito"
                      onClick={() => setModal(true)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {subscription &&
          [1, 2].includes(subscription.status || 0) &&
          subscription.criado.data().toISODate() !=
            DateTime.now().toISODate() && (
            <React.Fragment>
              <p className="text-center text-muted mt-3">
                Deseja cancelar seu período de teste? Basta clicar no botão
                abaixo.
                <br />
                <small>
                  Lembramos que não será possível reativar o período de teste
                  após o cancelamento.
                </small>
                <br />
              </p>
              <div className="d-flex justify-content-center mt-3">
                <div className="d-flex justify-content-center">
                  <Botao
                    lg
                    tema="danger"
                    confirmacao="Tem certeza que deseja cancelar a sua assintura? Você perderá o acesso a todos os cursos!"
                    onClick={cancelar}
                  >
                    Cancelar assinatura
                  </Botao>
                </div>
              </div>
            </React.Fragment>
          )}
        {subscription && subscription.status == 0 && (
          <>
            <p className="text-center text-muted mt-3">
              Gostaria de assinar novamente? Basta clicar no botão abaixo.
            </p>
            <div className="d-flex justify-content-center mt-3">
              <div className="d-flex justify-content-center">
                <BotaoLink lg to="/estudar/assinar/passar-cartao">
                  Assinar novamente
                </BotaoLink>
              </div>
            </div>
          </>
        )}
      </div>
      <Modal
        exibir={modal}
        aoFechar={setModal}
        titulo="Trocar cartão de crédito"
      >
        <Form onSubmit={trocar} regras={regras}>
          <Selecao
            name="bandeira"
            rotulo="Bandeira do cartão"
            opcoes={BANDEIRAS}
          />
          <Campo name="nome" rotulo="Nome como consta no cartão" />
          <Campo name="numero" rotulo="Número do cartão" />
          <div className="row">
            <div className="col-6">
              <Campo
                name="vencimento"
                rotulo="Dt. vencimento"
                mascara={MASCARA_DATA_VENCIMENTO}
                semMargem
              />
            </div>
            <div className="col-6">
              <Campo name="codigo" rotulo="Cód. segurança" semMargem />
            </div>
          </div>
          <div className="mt-3 d-flex">
            <Botao lg type="submit" className="w-100">
              Trocar cartão de crédito
            </Botao>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Visualizar;
